import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { useTaal } from "@/contexts/TaalContext";
import Licentie from "./Licentie";

/**
 * Instellingen > Organisatie (F1e, 15 oktober 2026).
 *
 * De installatie IS de organisatie (F1a, keuze van Frank: een peer per klant).
 * Hier staat haar naam en wie er lid is, met welke rol. Iedereen ziet dit; alleen
 * een eigenaar kan iets wijzigen. De regels staan in de backend
 * (`services/organisatie.py`), dit scherm laat alleen zien wat er mag: een knop
 * die een lid niet mag gebruiken, staat er voor hem niet.
 *
 * De teams staan ernaast zoals ze waren (besluit Frank): die zijn bedrijven
 * binnen een installatie, en dit is de klant die de installatie is.
 */

export const ROLLEN = ["eigenaar", "lid", "kijker"];

/** Een rol zoals een mens hem leest. Een onbekende rol leest als kijker, net als
 *  in de backend. */
export function rolInWoorden(t, rol) {
  if (rol === "eigenaar") return t("Eigenaar");
  if (rol === "lid") return t("Lid");
  return t("Kijker");
}

function fout(err, t) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : t("Dat lukte niet. Er is niets gewijzigd.");
}

export default function Organisatie() {
  const { t } = useTaal();
  const [organisatie, setOrganisatie] = useState(null);
  const [leden, setLeden] = useState([]);
  const [naam, setNaam] = useState("");
  const [dataThuis, setDataThuis] = useState("");
  const [grens, setGrens] = useState("");
  const [bezig, setBezig] = useState("");
  const [onleesbaar, setOnleesbaar] = useState(false);

  const laden = useCallback(async () => {
    try {
      const [{ data: org }, { data: lijst }] = await Promise.all([
        axios.get(`${API}/organisatie`),
        axios.get(`${API}/organisatie/leden`),
      ]);
      setOrganisatie(org);
      setNaam(org.naam || "");
      setDataThuis(org.data_thuis || "");
      setGrens(org.max_koppelingen_ingesteld ? String(org.max_koppelingen) : "");
      setLeden(Array.isArray(lijst) ? lijst : []);
      setOnleesbaar(false);
    } catch {
      setOnleesbaar(true);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  if (onleesbaar) {
    return <p className="text-sm text-muted-foreground" data-testid="organisatie-fout">
      {t("De organisatie kon niet worden geladen.")}
    </p>;
  }
  if (!organisatie) {
    return <Loader2 className="h-4 w-4 animate-spin" aria-label={t("Laden")} />;
  }

  const eigenaar = organisatie.mijn_rol === "eigenaar";

  const bewaarNaam = async () => {
    setBezig("naam");
    try {
      await axios.put(`${API}/organisatie`, { naam });
      toast.success(t("Naam bewaard"));
      await laden();
    } catch (err) {
      toast.error(fout(err, t));
    } finally {
      setBezig("");
    }
  };

  // F3a (Frank, 20 oktober 2026): het Nextcloud-adres als data-thuis, alleen een eigenaar.
  const bewaarDataThuis = async () => {
    setBezig("data-thuis");
    try {
      await axios.put(`${API}/organisatie/data-thuis`, { adres: dataThuis.trim() || null });
      toast.success(t("Adres bewaard"));
      await laden();
    } catch (err) {
      toast.error(fout(err, t));
    } finally {
      setBezig("");
    }
  };

  // CC1 (besluit Frank 24 oktober 2026): de grens op eigen koppelingen, per organisatie.
  const bewaarGrens = async () => {
    setBezig("grens");
    try {
      const waarde = grens.trim() === "" ? null : Number(grens);
      await axios.put(`${API}/organisatie/max-koppelingen`, { waarde });
      toast.success(t("Grens bewaard"));
      await laden();
    } catch (err) {
      toast.error(fout(err, t));
    } finally {
      setBezig("");
    }
  };

  const zetRol = async (lidId, rol) => {
    setBezig(lidId);
    try {
      await axios.put(`${API}/organisatie/leden/${lidId}/rol`, { rol });
      await laden();
    } catch (err) {
      toast.error(fout(err, t));
    } finally {
      setBezig("");
    }
  };

  return (
    <div className="space-y-6" data-testid="organisatie">
      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t("Organisatie")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("Deze installatie is een organisatie. Iedereen die hier inlogt, is lid met een rol.")}
        </p>
        {eigenaar ? (
          <div className="flex gap-2">
            <Input value={naam} onChange={(e) => setNaam(e.target.value)}
                   aria-label={t("Naam van de organisatie")} data-testid="organisatie-naam" />
            <Button onClick={bewaarNaam} disabled={bezig === "naam" || !naam.trim()}
                    data-testid="organisatie-naam-bewaar">
              {t("Bewaar")}
            </Button>
          </div>
        ) : (
          <p className="text-sm" data-testid="organisatie-naam-tekst">{organisatie.naam}</p>
        )}
      </div>

      <Licentie eigenaar={eigenaar} />

      {eigenaar && (
        <div className="space-y-2" data-testid="organisatie-data-thuis-blok">
          <h3 className="text-sm font-medium">{t("Data-thuis")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("Het Nextcloud-adres waar de data van deze organisatie thuis is. Het vult het koppelformulier voor; inloggen doet ieder lid zelf.")}
          </p>
          <div className="flex gap-2">
            <Input value={dataThuis} onChange={(e) => setDataThuis(e.target.value)} placeholder="https://"
                   aria-label={t("Nextcloud-adres (data-thuis)")} data-testid="organisatie-data-thuis" />
            <Button onClick={bewaarDataThuis} disabled={bezig === "data-thuis"}
                    data-testid="organisatie-data-thuis-bewaar">
              {t("Bewaar")}
            </Button>
          </div>
        </div>
      )}

      {eigenaar && (
        <div className="space-y-2" data-testid="organisatie-grens-blok">
          <h3 className="text-sm font-medium">{t("Koppelingen per lid")}</h3>
          <p className="text-xs text-muted-foreground">
            {t("Hoeveel eigen koppelingen een lid mag hebben. Standaard {standaard}, waarschuwing vanaf {waarschuwing}, hooguit {plafond}. Leeg is de standaard.",
              { standaard: 100, waarschuwing: organisatie.koppelingen_waarschuwing ?? 50,
                plafond: organisatie.koppelingen_plafond ?? 500 })}
          </p>
          <div className="flex gap-2">
            <Input type="number" min={1} max={organisatie.koppelingen_plafond ?? 500} value={grens}
                   onChange={(e) => setGrens(e.target.value)} placeholder={String(organisatie.max_koppelingen ?? 100)}
                   aria-label={t("Koppelingen per lid")} data-testid="organisatie-grens" />
            <Button onClick={bewaarGrens} disabled={bezig === "grens"} data-testid="organisatie-grens-bewaar">
              {t("Bewaar")}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <h3 className="text-sm font-medium">{t("Leden")}</h3>
        <ul className="divide-y rounded-md border" data-testid="organisatie-leden">
          {leden.map((lid) => (
            <li key={lid.id} className="flex items-center justify-between gap-2 px-3 py-2"
                data-testid={`lid-${lid.id}`}>
              <span className="text-sm min-w-0 truncate">
                {lid.naam || t("Zonder naam")}{lid.ik ? ` (${t("jij")})` : ""}
              </span>
              {eigenaar ? (
                <select value={lid.rol} disabled={bezig === lid.id}
                        onChange={(e) => zetRol(lid.id, e.target.value)}
                        aria-label={t("Rol van {naam}").replace("{naam}", lid.naam || "")}
                        className="h-9 rounded-md border bg-background px-2 text-sm"
                        data-testid={`rol-${lid.id}`}>
                  {ROLLEN.map((rol) => <option key={rol} value={rol}>{rolInWoorden(t, rol)}</option>)}
                </select>
              ) : (
                <span className="text-xs text-muted-foreground" data-testid={`rol-tekst-${lid.id}`}>
                  {rolInWoorden(t, lid.rol)}
                </span>
              )}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {t("Een eigenaar beheert de organisatie. Een lid werkt mee en bevestigt kaarten. Een kijker ziet alles maar bevestigt niets en start geen agent.")}
        </p>
      </div>
    </div>
  );
}
